import React, { useEffect, useRef, useState } from "react";
import { createMovieChatSession } from "./firebaseChat";
import "./moviechat.css";

const NAME_KEY = "hedone-chat-name";
const CLIENT_KEY = "hedone-chat-client-id";
const SPOILER_PATTERN = /\b(ending|final scene|post[- ]credits|killer is|turns out|dies|death of|secret identity|plot twist|revealed as|betrays)\b/i;

function getChatClientId() {
  const existing = localStorage.getItem(CLIENT_KEY);
  if (existing) return existing;
  const id = window.crypto?.randomUUID?.() || `device-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(CLIENT_KEY, id);
  return id;
}

function Initials({ name }) {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return <span className="movie-chat-avatar">{letters || "?"}</span>;
}

export default function MovieChat({
  movieId,
  movieTitle,
  mode = "page",
  playerControlsVisible = true,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showInvite, setShowInvite] = useState(mode === "page");
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) || "");
  const [nameDraft, setNameDraft] = useState(
    () => localStorage.getItem(NAME_KEY) || ""
  );
  const [people, setPeople] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  const [notification, setNotification] = useState(null);
  const [messageDraft, setMessageDraft] = useState("");
  const [markSpoiler, setMarkSpoiler] = useState(false);
  const [spoilerHint, setSpoilerHint] = useState(false);
  const [revealedSpoilers, setRevealedSpoilers] = useState(() => new Set());
  const [connectionState, setConnectionState] = useState("idle");
  const sessionRef = useRef(null);
  const conversationRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const typingTimeoutRef = useRef(null);
  const knownNotificationIdsRef = useRef(new Set());
  const inboxInitializedRef = useRef(false);
  const isOpenRef = useRef(isOpen);
  const selectedPersonRef = useRef(selectedPerson);
  const clientIdRef = useRef(getChatClientId());

  useEffect(() => {
    isOpenRef.current = isOpen;
    selectedPersonRef.current = selectedPerson;
  }, [isOpen, selectedPerson]);

  useEffect(() => {
    if (!name) return undefined;

    let cancelled = false;
    setConnectionState("connecting");

    createMovieChatSession({
      movieId,
      displayName: name,
      clientId: clientIdRef.current,
      onPeople: setPeople,
      onConnectionChange: (connected) =>
        setConnectionState(connected ? "online" : "connecting"),
    })
      .then((session) => {
        if (cancelled) {
          session.close();
          return;
        }
        sessionRef.current = session;
      })
      .catch(() => {
        if (!cancelled) setConnectionState("unavailable");
      });

    return () => {
      cancelled = true;
      const session = sessionRef.current;
      sessionRef.current = null;
      if (session) session.close().catch(() => {});
    };
  }, [movieId, name]);

  useEffect(() => {
    setSelectedPerson((current) => {
      if (!current) return current;
      return people.find((person) => person.uid === current.uid) || null;
    });
  }, [people]);

  useEffect(() => {
    if (!selectedPerson || !sessionRef.current) {
      setMessages([]);
      return undefined;
    }

    return sessionRef.current.subscribeToMessages(
      selectedPerson.uid,
      setMessages
    );
  }, [selectedPerson, connectionState]);

  useEffect(() => {
    if (!stickToBottomRef.current || !conversationRef.current) return;
    const frame = requestAnimationFrame(() => {
      const conversation = conversationRef.current;
      if (conversation) conversation.scrollTop = conversation.scrollHeight;
    });
    return () => cancelAnimationFrame(frame);
  }, [messages]);

  useEffect(() => {
    if (!selectedPerson || !sessionRef.current) {
      setIsOtherTyping(false);
      return undefined;
    }
    return sessionRef.current.subscribeToTyping(
      selectedPerson.uid,
      setIsOtherTyping
    );
  }, [selectedPerson, connectionState]);

  useEffect(() => {
    if (!sessionRef.current) return undefined;
    inboxInitializedRef.current = false;
    knownNotificationIdsRef.current = new Set();

    return sessionRef.current.subscribeToInbox((inbox) => {
      if (!inboxInitializedRef.current) {
        inbox.forEach((item) => {
          knownNotificationIdsRef.current.add(item.id);
          sessionRef.current?.acknowledgeNotification(item.id).catch(() => {});
        });
        const latest = inbox[inbox.length - 1];
        if (latest) {
          const conversationIsVisible =
            isOpenRef.current &&
            selectedPersonRef.current?.uid === latest.senderId;
          if (!conversationIsVisible) setNotification(latest);
        }
        inboxInitializedRef.current = true;
        return;
      }

      const newItems = inbox.filter(
        (item) => !knownNotificationIdsRef.current.has(item.id)
      );
      newItems.forEach((item) => {
        knownNotificationIdsRef.current.add(item.id);
        const conversationIsVisible =
          isOpenRef.current &&
          selectedPersonRef.current?.uid === item.senderId;
        if (!conversationIsVisible) setNotification(item);
        sessionRef.current?.acknowledgeNotification(item.id).catch(() => {});
      });
    });
  }, [connectionState]);

  useEffect(() => {
    if (!notification) return undefined;
    const timeout = setTimeout(() => setNotification(null), 6000);
    return () => clearTimeout(timeout);
  }, [notification]);

  useEffect(
    () => () => {
      clearTimeout(typingTimeoutRef.current);
    },
    []
  );

  const openChat = () => {
    setShowInvite(false);
    setIsOpen(true);
  };

  const saveName = (event) => {
    event.preventDefault();
    const cleanName = nameDraft.trim().replace(/\s+/g, " ").slice(0, 28);
    if (!cleanName) return;
    localStorage.setItem(NAME_KEY, cleanName);
    setName(cleanName);
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    const cleanText = messageDraft.trim();
    const text = `${markSpoiler ? "[spoiler] " : ""}${cleanText}`.slice(0, 500);
    if (!text || !selectedPerson || !sessionRef.current) return;

    setMessageDraft("");
    setMarkSpoiler(false);
    setSpoilerHint(false);
    clearTimeout(typingTimeoutRef.current);
    sessionRef.current.setTyping(selectedPerson.uid, false).catch(() => {});
    try {
      await sessionRef.current.sendMessage(selectedPerson.uid, text);
    } catch {
      setMessageDraft(text);
      setConnectionState("unavailable");
    }
  };

  const updateMessageDraft = (event) => {
    const value = event.target.value;
    setMessageDraft(value);
    if (SPOILER_PATTERN.test(value)) {
      setMarkSpoiler(true);
      setSpoilerHint(true);
    } else {
      setSpoilerHint(false);
    }
    if (!selectedPerson || !sessionRef.current) return;

    clearTimeout(typingTimeoutRef.current);
    sessionRef.current
      .setTyping(selectedPerson.uid, Boolean(value.trim()))
      .catch(() => {});
    if (value.trim()) {
      typingTimeoutRef.current = setTimeout(() => {
        sessionRef.current?.setTyping(selectedPerson.uid, false).catch(() => {});
      }, 1400);
    }
  };

  const openNotification = () => {
    const sender = notification;
    if (!sender) return;
    const onlinePerson = people.find(
      (person) => person.uid === sender.senderId
    );
    setSelectedPerson(
      onlinePerson || { uid: sender.senderId, name: sender.senderName }
    );
    setNotification(null);
    setShowInvite(false);
    setIsOpen(true);
  };

  return (
    <div
      className={`movie-chat-root is-${mode} ${
        playerControlsVisible ? "" : "is-controls-hidden"
      } ${isOpen ? "is-chat-open" : ""}`}
    >
      {notification && (
        <button
          className="movie-chat-notification"
          onClick={openNotification}
          aria-label={`New message from ${notification.senderName}`}
        >
          <Initials name={notification.senderName} />
          <span>
            <small>NEW MESSAGE</small>
            <strong>{notification.senderName}</strong>
            <p>{notification.text}</p>
          </span>
        </button>
      )}
      {showInvite && !isOpen && (
        <div className="movie-chat-invite" role="status">
          <button
            className="movie-chat-invite-close"
            onClick={() => setShowInvite(false)}
            aria-label="Dismiss live chat invitation"
          >
            ×
          </button>
          <span className="movie-chat-live-dot" />
          <div>
            <strong>Watching {movieTitle}?</strong>
            <p>See who else is here and chat live.</p>
          </div>
          <button className="movie-chat-join" onClick={openChat}>
            Join chat
          </button>
        </div>
      )}

      {isOpen && (
        <section className="movie-chat-panel" aria-label="Movie live chat">
          <header className="movie-chat-header">
            <div>
              <span className="movie-chat-eyebrow">LIVE ROOM</span>
              <h2>{selectedPerson ? selectedPerson.name : movieTitle}</h2>
            </div>
            <button onClick={() => setIsOpen(false)} aria-label="Close chat">
              ×
            </button>
          </header>

          {!name ? (
            <form className="movie-chat-name-form" onSubmit={saveName}>
              <span className="movie-chat-logo-mark">H</span>
              <h3>Join the conversation</h3>
              <p>
                Add a name so other people watching this movie can find you.
              </p>
              <label htmlFor="movie-chat-name">Your name</label>
              <input
                id="movie-chat-name"
                value={nameDraft}
                onChange={(event) => setNameDraft(event.target.value)}
                placeholder="e.g. Alex"
                autoComplete="nickname"
                maxLength={28}
                autoFocus
              />
              <button type="submit" disabled={!nameDraft.trim()}>
                Enter live room
              </button>
              <small>No account needed. Your name stays on this device.</small>
            </form>
          ) : (
            <div className="movie-chat-body">
              <aside className={selectedPerson ? "has-selection" : ""}>
                <div className="movie-chat-status-row">
                  <span>
                    <i className={`status-${connectionState}`} />
                    {connectionState === "unavailable"
                      ? "Chat unavailable"
                      : `${people.length + 1} online`}
                  </span>
                  <button
                    onClick={() => {
                      localStorage.removeItem(NAME_KEY);
                      setName("");
                      setNameDraft("");
                    }}
                  >
                    Edit name
                  </button>
                </div>
                <button className="movie-chat-person is-you" disabled>
                  <Initials name={name} />
                  <span>
                    <strong>{name}</strong>
                    <small>You</small>
                  </span>
                </button>
                {people.map((person) => (
                  <button
                    key={person.uid}
                    className={`movie-chat-person ${
                      selectedPerson?.uid === person.uid ? "is-active" : ""
                    }`}
                    onClick={() => setSelectedPerson(person)}
                  >
                    <span className="avatar-wrap">
                      <Initials name={person.name} />
                      <i />
                    </span>
                    <span>
                      <strong>{person.name}</strong>
                      <small>Online now</small>
                    </span>
                    <b>›</b>
                  </button>
                ))}
                {people.length === 0 && connectionState !== "unavailable" && (
                  <div className="movie-chat-empty-people">
                    <span>◎</span>
                    <strong>You’re first here</strong>
                    <p>Keep this open—new viewers will appear automatically.</p>
                  </div>
                )}
                {connectionState === "unavailable" && (
                  <div className="movie-chat-empty-people">
                    <span>!</span>
                    <strong>Can’t reach live chat</strong>
                    <p>Please try again in a moment.</p>
                  </div>
                )}
              </aside>

              <main className={selectedPerson ? "is-visible" : ""}>
                {selectedPerson ? (
                  <>
                    <button
                      className="movie-chat-back"
                      onClick={() => setSelectedPerson(null)}
                    >
                      ‹ Back to people
                    </button>
                    <div
                      className="movie-chat-conversation"
                      ref={conversationRef}
                      onScroll={(event) => {
                        const target = event.currentTarget;
                        stickToBottomRef.current =
                          target.scrollHeight -
                            target.scrollTop -
                            target.clientHeight <
                          36;
                      }}
                    >
                      <div className="movie-chat-conversation-intro">
                        <Initials name={selectedPerson.name} />
                        <strong>{selectedPerson.name}</strong>
                        <small>Both watching {movieTitle}</small>
                      </div>
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          className={`movie-chat-message ${
                            message.senderId === sessionRef.current?.uid
                              ? "is-mine"
                              : ""
                          }`}
                        >
                          {message.text?.startsWith("[spoiler] ") ? (
                            <button
                              type="button"
                              className={`movie-chat-spoiler ${revealedSpoilers.has(message.id) ? "is-revealed" : ""}`}
                              onClick={() => setRevealedSpoilers((current) => new Set([...current, message.id]))}
                              aria-label="Reveal spoiler message"
                            >
                              <span>{message.text.slice(10)}</span><b>Tap to reveal spoiler</b>
                            </button>
                          ) : <p>{message.text}</p>}
                          <small>
                            {message.createdAt
                              ? new Date(message.createdAt).toLocaleTimeString(
                                  [],
                                  { hour: "2-digit", minute: "2-digit" }
                                )
                              : "sending…"}
                          </small>
                        </div>
                      ))}
                      {isOtherTyping && (
                        <div className="movie-chat-typing" role="status">
                          <span /><span /><span />
                          {selectedPerson.name} is typing
                        </div>
                      )}
                    </div>
                    <form className="movie-chat-composer" onSubmit={sendMessage}>
                      {spoilerHint && <span className="movie-chat-spoiler-hint">Spoiler Shield detected plot-sensitive wording</span>}
                      <input
                        value={messageDraft}
                        onChange={updateMessageDraft}
                        placeholder={`Message ${selectedPerson.name}`}
                        maxLength={500}
                        aria-label={`Message ${selectedPerson.name}`}
                      />
                      <button
                        type="button"
                        className={`movie-chat-spoiler-toggle ${markSpoiler ? "is-active" : ""}`}
                        onClick={() => setMarkSpoiler((value) => !value)}
                        aria-pressed={markSpoiler}
                        title="Mark message as a spoiler"
                      >
                        !
                      </button>
                      <button
                        type="submit"
                        disabled={!messageDraft.trim()}
                        aria-label="Send message"
                      >
                        ↑
                      </button>
                    </form>
                  </>
                ) : (
                  <div className="movie-chat-select-prompt">
                    <span>↗</span>
                    <strong>Choose someone online</strong>
                    <p>Select a viewer to start a private conversation.</p>
                  </div>
                )}
              </main>
            </div>
          )}
        </section>
      )}

      <button
        className={`movie-chat-launcher ${isOpen ? "is-open" : ""}`}
        onClick={() => (isOpen ? setIsOpen(false) : openChat())}
        aria-label={isOpen ? "Close live chat" : "Open live chat"}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          "×"
        ) : mode === "player" ? (
          <>
            <span className="movie-chat-player-icon">•••</span>
            <em>Chat</em>
            <i />
          </>
        ) : (
          <>
            <span>•••</span>
            <i />
          </>
        )}
      </button>
    </div>
  );
}
